import { useEffect, useLayoutEffect, useRef, useState, type ReactNode } from 'react'

import { sectionTabs as sectionTabsFor } from '@/components/layout/nav-groups'
import { ShellSectionTabs } from '@/components/layout/SectionTabs'
import { useShellLocation } from '@/components/layout/shell-context'
import { SectionStripContext } from '@/components/ui/section-strip-context'
import BrokerRail from '@/features/broker-rail/BrokerRail'
import { isBrokerRoute } from '@/features/broker-rail/brokerRoute'
import MarketRail from '@/features/market-rail/MarketRail'
import PortfolioRail from '@/features/portfolio-rail/PortfolioRail'
import { isPortfolioRoute } from '@/features/portfolio-rail/portfolioRoute'
import RailFootnote from '@/features/market-rail/components/RailFootnote'
import { RailFitContext, useRailFit } from '@/features/market-rail/useRailFit'
import { useStickyRailOffset } from '@/features/market-rail/useStickyRailOffset'
import { useMitraStore } from '@/features/mitra/store'
import { isHandingOff, usePageFrame } from '@/lib/motion/page-frame'
import { closeStaggerAfterEntrance } from '@/lib/motion/stagger'
import { cn } from '@/lib/utils'

interface ScreenLayoutProps {
  /**
   * The title row: an eyebrow + h1, a family tab strip, a switcher row.
   *
   * It sits at the TOP OF THE MAIN COLUMN, not spanning both tracks. That is
   * what the V2 frames draw — at 1440 the title row starts at x=272 w=844 and
   * the rail's first card starts at the same y — and it is the whole reason
   * this is a slot rather than something a screen puts in `children` itself:
   * the gap between header and body is one value for every screen, and a
   * screen that forgets it drifts.
   */
  header?: ReactNode

  /**
   * The page's own actions: Alerts' "+ New alert". From `lg` up, under a section strip, they sit
   * at the right end of the strip's row, and the strip scrolls in the width they leave it. With
   * no strip (below `lg`, or a page outside a section) they sit at the right of the title row,
   * where TitleRow's own `actions` go. Drawn once and placed by CSS, so a control keeps its state
   * and focus when the window crosses `lg`.
   */
  actions?: ReactNode

  /** The main column. */
  children: ReactNode

  /**
   * The right rail. Defaults to the shared `<MarketRail />`, which is what V2
   * puts on every signed-in screen — and, under `/brokers`, to `<BrokerRail />`
   * (Today's highlights + Top rated brokers), and under `/portfolio` to `<PortfolioRail />`
   * (What's happening + Reminders + Portfolio health, rest:pa), picked by route so every
   * page of the section, its pending skeleton and its error page draw it alike.
   *
   * Pass your own node for a screen whose rail is genuinely about that screen
   * (Portfolio Overview's activity, a course's call to action). Pass
   * `fullWidth` to drop the track entirely.
   *
   * Either kind is fitted to the window the same way (`useRailFit`): a list
   * drawn through `RailRows` and a block marked with `useRailBlock` trim and
   * drop themselves, with nothing for the screen to thread through.
   */
  rail?: ReactNode

  /**
   * The page's own title — the TitleRow's words. The Ask Mitra bar names it
   * in its context chip and puts it in front of a question asked there
   * ("About Watchlist: …"), so Mitra knows which page it was asked from.
   * Published to the Mitra store while this page is up (`showPage`); a page
   * that names none is asked about by its section alone.
   */
  pageTitle?: string

  /**
   * The accessible name for a rail passed in through `rail`.
   *
   * Unused with the default rail: `<MarketRail />` is its own `<aside>` and
   * names itself, so the layout renders it into a bare track rather than
   * nesting a second complementary landmark around it.
   */
  railLabel?: string

  /**
   * No rail track at all: the main column takes the whole content box — 1136px
   * at V2's 1440, and every pixel `main` gives it elsewhere: nothing caps this
   * box any more (issue 13, 2026-09-29).
   *
   * For the screens V2 draws without one — the `(no shell)` onboarding, sign-in
   * and checkout frames, Account — and for the full-bleed tools (heatmap, the
   * wide floorsheet table) where a rail would leave the table unreadable.
   */
  fullWidth?: boolean

  /**
   * A one-line disclosure under a screen's OWN `rail`, drawn with
   * `RailFootnote`. The shared `<MarketRail />` has no footnote since the
   * 2026-09-28 design refresh (`4236:69879` draws no line under its last
   * block), so this is ignored on the default rail.
   */
  footnote?: string

  /**
   * This is a route's pending skeleton, not its page: the main column holds
   * skeleton blocks shaped like the loaded layout, and says so.
   *
   * Only the main column changes. The header slot and the rail render as they
   * would on the loaded page — pass the real TitleRow when the title is known
   * without data — which is what keeps a pending page from being a second,
   * differently-shaped page: the rail's data is already cached by the `_app`
   * loader, so it stands still through the whole load. The router draws a
   * pending page within a frame of the click, so the title row and rail enter
   * at once while the column's skeleton blocks wait 80ms before they fade in —
   * a fast load never flashes grey. The loaded page then takes the entrance
   * over rather than replaying it, and fades a copy of this column's skeleton
   * out over its own sections (`usePageFrame`). Keep everything static in
   * `header`, which must be ONE element: the column's first child is the one
   * that counts as frame.
   *
   * The column is `aria-busy` and marked `data-page-pending`. A status
   * announcing `pendingLabel` sits beside it (not inside it, on purpose — see
   * the effect near the bottom of the component) since every Skeleton is
   * `aria-hidden`. PendingPage is the default; OverviewScreen's
   * `OverviewPending` is the model for a route's own. This also resets scroll
   * to the top on mount — see the layout effect below — so a pending page
   * never draws at the position the LAST page left scroll at.
   */
  pending?: boolean
  /** What the pending status says to a screen reader. Default "Loading…". */
  pendingLabel?: string

  className?: string
  /** Override the main column's own stacking — its gap, mostly. */
  mainClassName?: string
}

/**
 * The V2 screen layout: a flexible main column and a rail that is 260px up to
 * about a 1950px window, then grows gently to 360px.
 *
 * This is the promotion of `TwoColumn`, which already had the geometry right
 * and which three screens used. V2 makes it the default path for every
 * signed-in screen, so the rail default, the header slot and the footnote slot
 * moved in with it and the old component is gone.
 *
 * Geometry, anchored on the V2 frames at 1440 and the nav-revamp mock the
 * owner approved: inside the 1136px content box the main column is 844, the
 * gutter 32 (`gap-8`) and the rail 260 (`--screen-rail-w`). The rail is 260
 * up to about a 1950 window, then grows with the viewport (`vw`, not the box,
 * so TopNav resolves the same width) to a 360 ceiling at about 2650; the main
 * column takes every other pixel (bug 11, 2026-10-17: a rail that grew with
 * the box, to ≈390 at a 2000 window, read as too wide; a fixed 260 was too
 * tight on ~2700). Nothing caps or centres the box either (issue 13,
 * 2026-09-29 — an earlier `--content-max-w` cap left empty bands beside
 * `main` past ≈1980px and the owner rejected it): it just fills `main`, at
 * every width and on every screen alike — `fullWidth` and the pending
 * skeletons included — so content edges never jump between two screens, or
 * between a skeleton and its page. There is no narrower cut of the rail and
 * no stacked fallback for it below.
 *
 * The rail is pinned on every screen, the shared one and a screen's own alike
 * (bug 11): only the main column travels with the page — the Twitter read.
 * There is no opt-out. A `stickyRail` prop used to leave a screen's own rail
 * unpinned unless it asked, and the owner wants the right column fixed
 * everywhere, so it is gone. The rail is ONE layout unit: every card in it
 * travels together and no card pins itself (see MarketRail).
 *
 * And it FITS (bug 12, 2026-10-17: "in any page the right side shouldn't be
 * scrollable"). The rail is never scrolled or clipped; it is made to fit
 * instead, between the pin and the window's foot, by `useRailFit`: rows give way
 * first, then whole blocks, in `RAIL_DROP_ORDER`. See the track's own note
 * below.
 *
 * Whether the rail TRACK exists at all is a straight AND of two things, per a
 * 2026-09-22 product call that overrides what this comment used to say (the
 * rail was "NOT hidden", just stacked full-width on a narrow box):
 *
 *   1. the VIEWPORT — not this component's own box — is at least Tailwind's
 *      `xl` (1280px); and
 *   2. the Ask Mitra panel is closed (`useMitraStore().open`).
 *
 * Below `xl`, or whenever Mitra is open, there is no rail: not stacked under
 * the main column, not shown narrower, gone. The main column takes the full
 * content width instead. That's a real behaviour change from the old "always
 * render it, somewhere" contract — a tablet member, and a member with the
 * Mitra panel open on a wide monitor, never see the market-summary cards in
 * this slot.
 *
 * The two halves of the rule are deliberately built two different ways:
 *  - the `xl` breakpoint is plain CSS (`hidden xl:block` below), so it
 *    resolves before first paint with no `useMediaQuery` hook and no
 *    re-render-after-mount flash;
 *  - Mitra-open is a JS condition that skips mounting the rail track's
 *    subtree — including `useStickyRailOffset`'s `ResizeObserver` — the
 *    instant the panel opens, which a boolean already sitting in a Zustand
 *    store costs nothing to read.
 *
 * None of that changes what a screen's own internal split sizes against: the
 * main column's width is still `xl` (or narrower) MINUS whatever the shell's
 * sidebar and Mitra tracks are taking, which no viewport breakpoint can see.
 * That's what the unnamed `@container` below is for, same as before — it
 * tracks the main column's own box, not the rail's visibility.
 *
 * It is also where a page's entrance lives (globals.css's PAGE ENTER): the
 * root fades in, and each direct child of the main column fades up in turn,
 * the rail's blocks on a stagger of their own. So a screen's top-level
 * sections are the units that enter; wrap two in one element and they enter
 * as one. The stagger plays once, for the sections the page mounts with: a
 * section that mounts later (a tab's panel, a card that appears with its
 * data) does not fade up as if the page had just arrived, and any section's
 * own `animate-*` class outranks it. The section tab strip is the exception
 * (`data-strip`): it stands still, at full opacity, through the whole
 * navigation, so where it is drawn neither the root nor the frame around it
 * fades — the rail and the rest of the frame do instead.
 */
export default function ScreenLayout({
  header,
  actions,
  children,
  rail,
  pageTitle,
  railLabel = 'Market summary',
  fullWidth = false,
  footnote,
  pending = false,
  pendingLabel = 'Loading…',
  className,
  mainClassName,
}: ScreenLayoutProps) {
  // `undefined` means "the screen said nothing", which V2 answers with the
  // shared rail. `fullWidth` is the only way to get no rail, so a screen can
  // never lose it by passing a falsy expression by accident.
  const sharedRail = rail === undefined

  // The Mitra-open half of the shown/hidden rule (see the header comment).
  // Read straight from the store rather than threaded down as a prop: every
  // screen using this layout already renders inside AppShell, which owns
  // this same boolean, so a prop would just be a second copy to keep in
  // sync rather than a real seam.
  const mitraOpen = useMitraStore((state) => state.open)

  // Whether the rail TRACK mounts at all. `fullWidth` and `mitraOpen` are
  // both known synchronously (a prop, a Zustand read), so gating on them in
  // JS is free and means the rail's own data hooks, and this hook's
  // `ResizeObserver`, never run while Mitra is open. The `xl` half of the
  // rule is deliberately NOT in this condition — it stays a CSS class below
  // instead of a third JS check here, so it resolves before first paint
  // instead of after a `useMediaQuery` re-render.
  const showRailTrack = !fullWidth && !mitraOpen

  const railTrackRef = useRef<HTMLDivElement>(null)
  // The space the rail has — the pin to the bottom clearance — and the pin
  // itself, which only leaves the header offset in the safety net
  // `computeStickyTop` describes. Gated on `showRailTrack` alone, since every
  // rail is pinned: no point measuring a rail that isn't mounted.
  const { top: measuredTop, availableHeight: railAvailableHeight } = useStickyRailOffset(
    railTrackRef,
    showRailTrack,
  )
  // Every rail, the shared one and a screen's own, is fitted to that height
  // here, once: its blocks read the answer from `RailFitContext`.
  const railFit = useRailFit(railTrackRef, railAvailableHeight, showRailTrack)

  // The page's title, for the Ask Mitra bar. A layout effect, so the bar
  // names this page from its first paint, never the one before it.
  const showPage = useMitraStore((state) => state.showPage)
  useLayoutEffect(() => {
    if (pageTitle === undefined) return
    return showPage(pageTitle)
  }, [pageTitle, showPage])

  // The pending page can mount scrolled far down the OLD page's position:
  // the router only resets scroll in `onRendered`, which fires once the NEW
  // page's content has committed — nothing runs it for the skeleton in
  // between. Measured: a member 1400px down /market landed on /stocks/NABIL
  // with the pending page's header skeleton drawn 1114px above the
  // viewport, then snapping to 0 the instant the content landed. A layout
  // effect fires before paint, so the pending page's very first frame is
  // always at the top instead. Runs once, at mount — a route's pending
  // component is a fresh instance every time it appears, never a `pending`
  // flip on one already mounted — and only when this instance IS the
  // pending page. Back/forward restoration and `shouldRestoreScroll`
  // (app/router.tsx) both act on the LOADED page, after commit, so neither
  // races this; a search-param change never renders a pending ScreenLayout
  // at all (no route re-runs its loader for one), so it is unaffected.
  useLayoutEffect(() => {
    if (pending) window.scrollTo(0, 0)
    // eslint-disable-next-line react-hooks/exhaustive-deps -- mount only, see comment above
  }, [])

  // The pending page hands its column over as well as its frame: a copy of
  // its skeleton fades out over the loaded sections as they arrive, and those
  // rise less (`data-handoff`). Decided in the first render, while the pending
  // page is still up to ask, and kept: the mark is for this mount's entrance.
  const rootRef = useRef<HTMLDivElement>(null)
  const columnRef = useRef<HTMLDivElement>(null)
  usePageFrame(rootRef, columnRef, pending)
  const [handingOff] = useState(() => !pending && isHandingOff())

  // The sections' stagger ends once it has played (lib/motion/stagger.ts).
  // A pending column has no stagger to end: its skeleton fades in as one.
  useEffect(() => {
    const column = columnRef.current
    if (pending || column === null) return
    return closeStaggerAfterEntrance(column)
  }, [pending])

  // The rail's, each time its track mounts: closing the Mitra panel brings
  // the track back, and its blocks enter again then.
  useEffect(() => {
    const rail = railTrackRef.current?.firstElementChild
    if (!(rail instanceof HTMLElement)) return
    return closeStaggerAfterEntrance(rail)
  }, [showRailTrack])

  // The page's section tab strip, a pure function of the URL — so the pending
  // page, the error page and the loaded page all draw the same one, and it
  // never waits on anything (but the Alerts counts, which it fills in).
  // Outside the shell there is no URL to read and no strip.
  const location = useShellLocation()
  const strip = location === null ? null : sectionTabsFor(location)
  // The shared rail's Broker cut, read off the same shell URL (none outside the shell).
  const brokerRail = location !== null && isBrokerRoute(location.pathname)
  const portfolioRail = location !== null && isPortfolioRoute(location.pathname)

  // The title row carries on across a pending page's handoff while the
  // sections under it enter fresh, and CSS can only tell it apart as the
  // column's first child — so it has to know there is one. The strip and the
  // page's actions are part of it: one element, so it stays the first child.
  const ownHeader = header !== undefined && header !== null && typeof header !== 'boolean'
  const ownActions = actions !== undefined && actions !== null && typeof actions !== 'boolean'
  const hasHeader = ownHeader || strip !== null || ownActions
  // The strip and its hairline span the whole main column at every width, rail or no rail, as the
  // mock's do (owner F6, 2026-10-29; it used to stop at 844 beside the rail).
  const stripNode =
    location === null || strip === null ? null : (
      <ShellSectionTabs
        location={location}
        model={strip}
        className="lg:col-start-1 lg:row-start-1"
      />
    )
  const headerNode =
    ownHeader && stripNode !== null ? (
      <SectionStripContext value={true}>{header}</SectionStripContext>
    ) : (
      header
    )
  const frame = ownActions ? (
    // One grid for both layouts, so the actions are one node at every width. From `lg` up under
    // a strip: the strip and the actions share the first row, its hairline carried under the
    // actions (no column gap to break it), and the header spans the row below. Otherwise the strip is not drawn and the header and the
    // actions share the first row, as a TitleRow's own title and actions do. `contents` lets the
    // header's own element take the grid placement.
    <div
      className={cn(
        'grid grid-cols-[minmax(0,1fr)_auto] gap-x-3 gap-y-4',
        stripNode !== null && 'lg:-mb-2 lg:gap-x-0',
      )}
    >
      {stripNode}
      <div
        className={cn(
          'col-start-2 row-start-1 flex items-center gap-2 self-start',
          // rest:kit — `pt-4` steps past the section label above the strip (ShellSectionTabs:
          // 16 tall, pulled up 4 and 4 off the strip), so the actions centre on the strip's row.
          stripNode !== null &&
            'lg:self-stretch lg:border-b lg:border-border-subtle lg:pt-4 lg:pl-6',
        )}
      >
        {actions}
      </div>
      {ownHeader ? (
        <div
          // PAGE ENTER reaches through it to the header: a `contents` box can't fade.
          data-frame-contents=""
          className={cn(
            'contents *:col-start-1 *:row-start-1',
            stripNode !== null && 'lg:*:col-span-full lg:*:row-start-2',
          )}
        >
          {headerNode}
        </div>
      ) : null}
    </div>
  ) : stripNode === null ? (
    header
  ) : (
    // 16 under the strip's hairline to whatever follows: the gap inside,
    // and the column's 24 less 8 after it. A TitleRow under the strip
    // keeps its `h1` for screen readers only from `lg` up (out of flow,
    // so no gap), and below `lg`, where the strip is not drawn, shows as
    // it always has — this wrapper goes too when it has nothing else.
    <div className={cn('flex flex-col gap-4 lg:-mb-2', !ownHeader && 'max-lg:hidden')}>
      {stripNode}
      {ownHeader ? headerNode : null}
    </div>
  )

  // The pending status is filled in a render AFTER this mounts, not the one
  // that turns `pending` on: a `role="status"` born with its words already
  // there gives a screen reader's mutation observer nothing to see — it only
  // starts watching once it has attached, which for a page that opens
  // straight onto its own pending skeleton is after this element already
  // exists. The effect below is the fill, one render later, which IS a real
  // change to announce — the same reasoning UpdatingNote gives for staying
  // mounted through its own idle case rather than arriving already spoken
  // for. The span this feeds also sits OUTSIDE the `aria-busy` column below:
  // several screen readers hold back a live region's announcement for as
  // long as its container claims `aria-busy="true"`, which here is the
  // whole pending page.
  const [pendingAnnounced, setPendingAnnounced] = useState(false)
  useEffect(() => {
    // Direct, not deferred to a microtask or moved to a `useLayoutEffect`:
    // a screen reader needs a GENUINE second paint, so the browser must
    // actually show the span empty before this fills it in. A layout
    // effect resolves before the browser paints anything, which collapses
    // both states into the one paint this exists to split apart. A
    // microtask-deferred update (the shape MitraPanel's `checkTokenOnOpen`
    // uses) is real in a browser but never settles inside a synchronous
    // test — confirmed against a card-level equivalent of this same fix
    // (see CardState). A plain synchronous update inside a passive effect
    // is the one shape that is a true two-paint reveal in the browser and
    // still settles by the time `render()`'s `act()` flush returns in a
    // test — which is what lets `StockScreen.test.tsx` find this status by
    // its eventual name with a plain `findByRole`.
    // eslint-disable-next-line react-hooks/set-state-in-effect -- see above
    if (pending) setPendingAnnounced(true)
  }, [pending])

  return (
    // No more `@container/screen`: the rail track's own visibility no longer
    // reads this box (see the header comment) — it reads the viewport, via
    // `xl:` below — so there is nothing left that needs a named container to
    // pin variants to. The main column keeps its own, separate, UNNAMED
    // `@container` a few lines down; that one is still real.
    <div
      ref={rootRef}
      data-strip={stripNode !== null || undefined}
      className={cn('page-enter', className)}
    >
      {pending ? (
        <span role="status" className="sr-only">
          {pendingAnnounced ? pendingLabel : null}
        </span>
      ) : null}
      <div
        className={cn(
          'grid gap-8',
          showRailTrack && 'xl:grid-cols-[minmax(0,1fr)_var(--screen-rail-w)]',
        )}
      >
        {/* `min-w-0` is what stops a wide table inside a screen from pushing the
            main track past its share of the grid.

            `@container` is what lets a screen's own internal split size against
            THIS track rather than the viewport. It has to be a container query
            and not a breakpoint, because the track width is a function of
            independent things — the viewport, whether the sidebar is collapsed,
            and whether the Mitra panel is open — so no `lg:` can name it. A
            nested `lg:grid-cols-[minmax(0,1fr)_360px]` (the shape Market
            Overview and Sectors used while the body was the full 1112px box)
            resolves to `0px 360px` and overflows once this track is narrower
            than that at a 1024 window with the Mitra panel open. Measured, not
            reasoned. This still holds with the rail hidden below `xl` or with
            Mitra open — the main column's own width is never just `100vw`.

            Safe to contain: nothing inside a screen is `position: fixed`. The
            overlays are either a native `<dialog showModal()>` (Modal, Sheet —
            top layer, outside containment) or portalled to `<body>` (Popover,
            Tooltip), and ToastHost is mounted up in AppShell. */}
        <div
          ref={columnRef}
          // The page region: the main column, whose sections enter in turn
          // (or, pending, whose skeleton waits and fades in). See PAGE ENTER
          // in globals.css.
          data-page-region
          data-page-pending={pending || undefined}
          data-has-header={hasHeader || undefined}
          data-handoff={handingOff || undefined}
          aria-busy={pending || undefined}
          className={cn(
            'page-column @container flex min-w-0 flex-col gap-6',
            !pending && 'stagger',
            mainClassName,
          )}
        >
          {frame}
          {children}
        </div>

        {showRailTrack && (
          // The track, not the landmark. `<MarketRail />` is already an
          // `<aside>` and names itself, so wrapping it in a second one would
          // nest two complementary landmarks and make a screen reader announce
          // the rail twice. A rail a screen hands in is plain content and gets
          // the landmark here instead.
          //
          // Mounted whenever `showRailTrack` is true (`!fullWidth &&
          // !mitraOpen`) and hidden below `xl` with plain CSS — `hidden
          // xl:block` — rather than a second JS condition. That trades a
          // rail that keeps fetching while invisible on a tablet for a
          // breakpoint that resolves before first paint with no
          // `useMediaQuery` flash; the Mitra half of the rule already pays
          // for correctness by not mounting this subtree at all.
          <div
            ref={railTrackRef}
            // The Ask Mitra bar stops short of a rail that is drawn
            // (MitraDock): it reads this mark through `:has()`.
            data-screen-rail=""
            className={cn(
              'hidden xl:block',
              // `self-start` is load-bearing: a grid item stretches to the row
              // by default, and a sticky box as tall as its row never has
              // anywhere to stick.
              //
              // `top` only — no `bottom`, no `max-height`, no
              // `overflow-y-auto`. This class sets the plain pin
              // (`--header-h` + `--content-top`, level with the main
              // column's first thing), which is where every rail sits:
              // `useRailFit` trims rows and drops blocks until it fits above
              // the window's foot, at any window 640px tall or more. The inline
              // `top` below only ever differs in `computeStickyTop`'s safety
              // net, a shorter window whose rail has nothing left to drop.
              //
              // A measured `max-height` + `xl:overflow-y-auto` shipped here
              // for one revision and the owner rejected it on sight: a rail
              // that scrolls inside a page that also scrolls is two
              // scrollbars arguing over one gesture. The rail's own box never
              // scrolls; it fits.
              'xl:sticky xl:self-start',
              'xl:top-[calc(var(--header-h)+var(--content-top))]',
            )}
            // Only ever consulted by the browser once `position` resolves to
            // `sticky` — i.e. only at `xl` and up, where the class above
            // applies it. Below `xl` the element is `display: none` and this
            // is inert, so there is nothing to gate here a second time.
            style={measuredTop === null ? undefined : { top: `${String(measuredTop)}px` }}
          >
            <RailFitContext value={railFit}>
              {sharedRail ? (
                brokerRail ? (
                  <BrokerRail className="stagger" />
                ) : portfolioRail ? (
                  <PortfolioRail className="stagger" />
                ) : (
                  <MarketRail className="stagger" />
                )
              ) : (
                <aside aria-label={railLabel} className="stagger flex flex-col gap-4">
                  {rail}
                  {footnote !== undefined && <RailFootnote>{footnote}</RailFootnote>}
                </aside>
              )}
            </RailFitContext>
          </div>
        )}
      </div>
    </div>
  )
}
